import * as Run from '../../util/runs';
import {type ScatterPlotConfig} from '../PanelScatterPlot/common';

export type ReferenceLineAxis = 'x' | 'y';

export type ReferenceLinesValidationConfig = Pick<
  ScatterPlotConfig,
  'referenceLines' | 'xAxis' | 'yAxis' | 'xAxisLogScale' | 'yAxisLogScale'
>;

export interface ReferenceLineConfig {
  axis: ReferenceLineAxis;
  value?: number;
  label?: string;
  color?: string;
}

export interface ReferenceLine extends ReferenceLineConfig {
  value: number;
}

export const MAX_REFERENCE_LINES = 5;

export function referenceLineAxisIsSupported(
  axis: string | undefined
): boolean {
  return axis != null && !Run.isTimeKeyString(axis);
}

function referenceLineHasValidValue(
  line: ReferenceLineConfig
): line is ReferenceLine {
  return line.value != null && Number.isFinite(line.value);
}

export type ReferenceLineValidationContext = {
  xAxisSupportsReferenceLines: boolean;
  yAxisSupportsReferenceLines: boolean;
  xAxisLogScale: boolean;
  yAxisLogScale: boolean;
};

export function getReferenceLineValidationContext(
  config: ReferenceLinesValidationConfig
): ReferenceLineValidationContext {
  return {
    xAxisSupportsReferenceLines: referenceLineAxisIsSupported(config.xAxis),
    yAxisSupportsReferenceLines: referenceLineAxisIsSupported(config.yAxis),
    xAxisLogScale: config.xAxisLogScale ?? false,
    yAxisLogScale: config.yAxisLogScale ?? false,
  };
}

export function isReferenceLineValid(
  line: ReferenceLineConfig,
  context: ReferenceLineValidationContext
): line is ReferenceLine {
  const axisSupported =
    line.axis === 'x'
      ? context.xAxisSupportsReferenceLines
      : context.yAxisSupportsReferenceLines;

  if (!axisSupported) {
    return false;
  }

  if (!referenceLineHasValidValue(line)) {
    return false;
  }

  const usesLogScale =
    line.axis === 'x' ? context.xAxisLogScale : context.yAxisLogScale;
  if (usesLogScale && line.value <= 0) {
    return false;
  }

  return true;
}

export function referenceLinesAreValid(
  config?: ReferenceLinesValidationConfig
): boolean {
  if (config?.referenceLines == null) {
    return true;
  }

  const context = getReferenceLineValidationContext(config);

  return config.referenceLines.every(line =>
    isReferenceLineValid(line, context)
  );
}
